// src/components/Organization/DisabledEmailOverlay.tsx

// What? A shared wrapper that dims (but does not hide) email admin controls
//       when an organization's `email_enabled` kill switch is off.
// Why?  Admins still need to read existing layout/template configuration when
//       email is disabled; hiding the UI creates confusion about where
//       settings went. Dim + banner communicates state without blocking.
// How?  Warning alert above, plus a Box wrapping children with reduced opacity
//       and pointer-events disabled so controls are read-only while off.

import React from "react";
import { Alert, Box, Link, Stack } from "@mui/joy";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

interface DisabledEmailOverlayProps {
  children: React.ReactNode;
  reason?: string;
  contextLink?: { label: string; href: string };
}

const DEFAULT_REASON =
  "Email sending is disabled for this organization. Changes here will be saved but nothing will be sent until email is re-enabled.";

const DisabledEmailOverlay: React.FC<DisabledEmailOverlayProps> = ({
  children,
  reason,
  contextLink,
}) => {
  return (
    <Stack spacing={1.5}>
      <Alert
        color="warning"
        variant="soft"
        size="sm"
        startDecorator={<WarningAmberIcon />}
      >
        <Box>
          {reason || DEFAULT_REASON}
          {contextLink && (
            <>
              {" "}
              <Link href={contextLink.href} level="body-sm">
                {contextLink.label}
              </Link>
            </>
          )}
        </Box>
      </Alert>
      <Box
        sx={{
          opacity: 0.6,
          pointerEvents: "none",
          userSelect: "none",
        }}
        aria-disabled="true"
      >
        {children}
      </Box>
    </Stack>
  );
};

export default DisabledEmailOverlay;
